import {
	offset,
	shift,
	useClientPoint,
	useFloating,
	useHover,
	useInteractions,
} from "@floating-ui/react";
import { moment } from "@msuite/utils/date";
import clsx from "clsx";
import React, { useMemo } from "react";
import { Divider } from "../../divider";
import { useDisclosure } from "../../hooks";
import { Portal } from "../../portal";

export interface ActivityChartData<T> {
	date: string; // YYYY-MM-DD
	item: T;
}

export interface ActivityChartCellProps<T> {
	date: string; // YYYY-MM-DD
	data: Array<ActivityChartData<T>>;
	onDateClick?: (date: string) => void;
	renderTooltip?: (items: Array<ActivityChartData<T>>) => React.ReactNode;
	maxValue: number;
}

export function ActivityChartCell<T>({
	data,
	date,
	onDateClick = () => {},
	renderTooltip,
	maxValue,
}: ActivityChartCellProps<T>) {
	/** Hooks */
	const disclosure = useDisclosure();
	const { refs, floatingStyles, context } = useFloating({
		open: disclosure.isOpen,
		onOpenChange: disclosure.setIsOpen,
		placement: "bottom",
		middleware: [offset(10), shift({ padding: 10 })],
	});

	const hover = useHover(context);
	const clientPoint = useClientPoint(context);
	const { getFloatingProps, getReferenceProps } = useInteractions([hover, clientPoint]);

	/** Data */
	const items = data.filter((item) => item.date === date);
	const hasItems = items.length > 0;
	const percentageOfMax = items.length / maxValue;

	const color = useMemo(() => {
		if (percentageOfMax < 0.25) {
			return "var(--color-accent-300)";
		} else if (percentageOfMax < 0.5) {
			return "var(--color-accent-400)";
		} else if (percentageOfMax < 0.75) {
			return "var(--color-accent-500)";
		} else {
			return "var(--color-accent-600)";
		}
	}, [percentageOfMax]);

	return (
		<React.Fragment>
			<div
				className="p-[0.5px] w-full aspect-square cursor-pointer"
				onClick={() => {
					onDateClick?.(date);
				}}
				{...getReferenceProps({ ref: refs.setReference })}
			>
				<div
					className={clsx(
						"w-full h-full",
						"activity-chart-cell",
						!hasItems ? "bg-gray-200 opacity-50 dark:opacity-10" : "",
					)}
					style={{ backgroundColor: hasItems ? color : undefined }}
				/>
			</div>
			{disclosure.isOpen && (
				<Portal>
					<div
						style={floatingStyles}
						{...getFloatingProps({ ref: refs.setFloating })}
						className="z-50 absolute"
					>
						<div className="card v-stack">
							<p className="text-xs">{moment(date).dateFormat()}</p>
							{renderTooltip && hasItems ? (
								<React.Fragment>
									<Divider variant="card" />
									{renderTooltip?.(items)}
								</React.Fragment>
							) : null}
						</div>
					</div>
				</Portal>
			)}
		</React.Fragment>
	);
}
